/**
 * What somebody who is not staff sees — 29 Aug 2026.
 *
 * Three roles were added to the tool on the same day, and none of them is here
 * to use the CRM: an external salesperson works the leads assigned to them, a
 * shareholder reads their own holding and the cap table, an ambassador keeps
 * their own record up to date. So each gets one screen rather than a navigation
 * they would find four fifths of locked.
 *
 * **The rules are the boundary, not this file.** `firestore.rules` refuses
 * everything these screens do not need, and would refuse it just as firmly if
 * this component were wrong. What this adds is an answer instead of a wall of
 * failed reads — which is a design question, not a security one.
 */
import { useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import type { CrmRole } from '@/types/crm'
import { AccountMenu } from '@/features/auth/AccountMenu'
import { ShareholderScreen } from './ShareholderScreen'
import { ShareholderHome } from './ShareholderHome'
import { AmbassadorScreen } from './AmbassadorScreen'
import { ExternalSalesScreen } from './ExternalSalesScreen'
import './OutsideApp.css'

/**
 * The tabs an outside login can be offered, in the order they appear.
 *
 * Keyed separately from the role, because since 17 Sep 2026 one role can offer
 * more than one: a shareholder lands on Home and finds their numbers next door.
 * Home is first, and first is the landing page — somebody coming back weekly is
 * not coming back for the cap table.
 */
const SCREENS: { key: string; role: CrmRole; label: string }[] = [
  { key: 'home', role: 'shareholder', label: 'Home' },
  { key: 'shares', role: 'shareholder', label: 'Your shares' },
  { key: 'externalSales', role: 'externalSales', label: 'Your leads' },
  { key: 'ambassador', role: 'ambassador', label: 'Your details' },
]

export function OutsideApp() {
  const { roles, user } = useAuth()

  /**
   * One screen per role this login actually holds — 29 Aug 2026, second pass.
   *
   * A person can be an external salesperson and a shareholder at once, so the
   * shell shows what they hold rather than the first thing that matched. With
   * one role there is nothing to choose between and no tabs appear: a switcher
   * with a single option is furniture.
   */
  const mine = SCREENS.filter((s) => roles.includes(s.role))
  const [open, setOpen] = useState<string>(mine[0]?.key ?? 'home')
  const current = mine.some((s) => s.key === open) ? open : mine[0]?.key

  return (
    <main className="outside">
      <header className="outside__head">
        <div>
          <div className="outside__brand">Skida</div>
          <div className="outside__who">{user?.displayName || user?.email}</div>
        </div>
        <AccountMenu />
      </header>

      {mine.length > 1 && (
        <div className="outside__tabsbar">
          <nav className="outside__tabs">
            {mine.map((s) => (
              <button
                key={s.key}
                className={`chip${current === s.key ? ' chip--on' : ''}`}
                aria-pressed={current === s.key}
                onClick={() => setOpen(s.key)}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="outside__body">
        {current === 'home' && <ShareholderHome />}
        {current === 'shares' && <ShareholderScreen />}
        {current === 'ambassador' && <AmbassadorScreen />}
        {current === 'externalSales' && <ExternalSalesScreen />}
        {/*
          On the allowlist and holding nothing that has a screen. It should not
          happen — the editor offers a closed set — but a blank page is the one
          outcome that looks like the tool is broken, so it says something.
        */}
        {!current && (
          <p className="empty-note">
            Your account is signed in but has nothing assigned to it yet. Ask whoever invited you to
            finish setting it up.
          </p>
        )}
      </div>
    </main>
  )
}
